import { useSelector } from 'react-redux';

import { MirrorService } from 'services/MirrorService';
import { Package } from 'types/interfaces/Package';

import { usePackPrice } from 'hooks/usePackagePrice';
import {
  is3dPackageEnabledSelector,
  isCarouselPaywallEnabledSelector,
  isPaywallMonthPackageEnabledSelector,
} from 'store/common/selectors';
import { isFreeTrialPackageThemeSelector } from 'store/payment/selectors';

const getDiscountedPriceText = (
  discounted: string,
  isTrialThemeAndPackIsFree: boolean
) => {
  return discounted && isTrialThemeAndPackIsFree
    ? `Then ${discounted}`
    : discounted;
};

const getPriceFullInitial = (
  initial: string,
  initialByPeriod: string,
  recurring: string,
  isLegal: boolean
) => {
  if (initial === initialByPeriod && isLegal) return recurring;

  return initial;
};

export const usePremiumPackPrice = (pack?: Package) => {
  const isFreeTrialPackageTheme = useSelector(isFreeTrialPackageThemeSelector);

  const isCarouselPaywallEnabled = useSelector(
    isCarouselPaywallEnabledSelector
  );
  const is3dPackageEnabled = useSelector(is3dPackageEnabledSelector);
  const isStopExistEnable = useSelector(isPaywallMonthPackageEnabledSelector);

  const packPrice = usePackPrice({ package: pack });

  const isHasDiscount = packPrice?.isHasDiscount;
  const isTrialThemeAndPackIsFree =
    isFreeTrialPackageTheme && Boolean(pack?.is_free) && !is3dPackageEnabled;
  const discount = packPrice?.discount;
  const durationLabel = packPrice?.durationLabel;
  const recurringPrice = packPrice?.applyCurrency(
    String(packPrice?.recurringInitial)
  );

  const initialPackagePrice = MirrorService.isMidSite
    ? `${packPrice?.applyCurrency(packPrice?.initial)}`
    : `${packPrice?.applyCurrency(
        packPrice?.initialByPeriod
      )}/${durationLabel}`;

  const priceByPeriod = MirrorService.isMidSite
    ? `${packPrice?.applyCurrency(
        isTrialThemeAndPackIsFree ? '0' : String(packPrice?.afterDiscount)
      )}`
    : `${packPrice?.applyCurrency(
        isTrialThemeAndPackIsFree
          ? '0'
          : String(packPrice?.afterDiscountByPeriod)
      )}/${durationLabel}`;

  const isCloneSite = MirrorService.isLocalsJoy || MirrorService.isLustStation;
  const priceAdditional =
    isHasDiscount && (isTrialThemeAndPackIsFree || !isCloneSite)
      ? getDiscountedPriceText(initialPackagePrice, isTrialThemeAndPackIsFree)
      : null;

  const customRoundedPriceByPeriod = () => {
    const priceMap: Record<string, string> = {
      '1.43': '1.40',
      '1.42': '1.40',
      '1.00': '0.99',
      '0.98': '0.99',
      '0.71': '0.70',
    };

    const basePrice = packPrice?.afterDiscountByPeriod;
    const price = basePrice ? priceMap[basePrice] ?? basePrice : '0';
    const finalPrice = isTrialThemeAndPackIsFree ? '0' : String(price);

    return `${packPrice?.applyCurrency(finalPrice)}/${durationLabel}`;
  };

  const customRoundedPriceAdditional = () => {
    const priceMap: Record<string, string> = {
      '1.43': '1.40',
      '1.42': '1.40',
    };

    const basePrice = packPrice?.initialByPeriod;
    const price = basePrice ? priceMap[basePrice] ?? basePrice : basePrice;
    const priceWithCurrency = `${packPrice?.applyCurrency(
      price
    )}/${durationLabel}`;

    return isHasDiscount && (isTrialThemeAndPackIsFree || !isCloneSite)
      ? getDiscountedPriceText(priceWithCurrency, isTrialThemeAndPackIsFree)
      : null;
  };

  const priceByPeriodAfterDiscount = `${packPrice?.applyCurrency(
    String(packPrice?.afterDiscount)
  )}`;

  const priceFullInitialLegal = `${packPrice?.applyCurrency(
    String(
      getPriceFullInitial(
        packPrice?.initial,
        packPrice?.initialByPeriod,
        packPrice?.recurringInitial,
        isCarouselPaywallEnabled
      )
    )
  )}`;

  const priceFullInitialReal = `${packPrice?.applyCurrency(
    String(packPrice?.initial)
  )}`;

  return {
    priceByPeriod: isStopExistEnable
      ? customRoundedPriceByPeriod()
      : priceByPeriod,
    priceByPeriodAfterDiscount,
    priceFullInitialLegal,
    priceFullInitialReal,
    durationLabel,
    priceAdditional: isStopExistEnable
      ? customRoundedPriceAdditional()
      : priceAdditional,
    isHasDiscount,
    isTrialThemeAndPackIsFree,
    discount,
    recurringPrice,
  };
};
